import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { config } from './config';

// API base URL is configured via environment variables

const client = axios.create({
  baseURL: config.API_BASE_URL,
  responseType: 'json',
  timeout: 20000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
    // Authorization removed - set dynamically via interceptor
  },
});

// Add request interceptor to dynamically set Authorization header
// Only set if not already set (to allow custom headers like in getMe)
client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const formClient = axios.create({
  baseURL: config.API_BASE_URL,
  responseType: 'json',
  timeout: 10000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'multipart/form-data',
    // Authorization removed - set dynamically via interceptor
  },
});

// Add request interceptor to dynamically set Authorization header
// Only set if not already set (to allow custom headers)
formClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const documentClient = axios.create({
  baseURL: config.API_BASE_URL,
  responseType: 'blob',
  timeout: 10000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
    // Authorization removed - set dynamically via interceptor
  },
});

// Add request interceptor to dynamically set Authorization header
// Only set if not already set (to allow custom headers)
documentClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const request = async (options) => {
  const onSuccess = (response) => {
    console.debug('Request Successful!', response);
    return response.data;
  };
  const onError = (error) => {
    console.error('Request Failed:', error.toJSON());
    if (error.response?.status === 401) {
      // Save current URL to localStorage before redirecting (fallback for 401 errors)
      // Router.jsx uses query params, but we can't add them with window.location.href
      const currentPath = window.location.pathname + window.location.search;
      const email = localStorage.getItem('email');
      localStorage.clear();
      if (email) {
        localStorage.setItem('email', email);
      }
      // Store returnUrl in localStorage as fallback (Login.tsx checks query params first)
      localStorage.setItem('returnUrl', currentPath);
      window.location.href = '/login';
    }
    return Promise.reject(error.response?.data || error.message);
  };

  return client({ ...options })
    .then(onSuccess)
    .catch(onError);
};

export const useApiGet = (key, fn, options, stringParams = null) =>
  useQuery({
    queryKey: key,
    queryFn: stringParams ? () => fn(stringParams) : () => fn(),
    ...options,
  });

export const useApiSend = (fn, success, error, invalidateKey, options) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      invalidateKey &&
        invalidateKey.forEach((key) => {
          queryClient.invalidateQueries(key);
        });
      success && success(data);
    },
    onError: error,
    retry: 0,
    ...options,
  });
};
